import React, { useState, useEffect, useCallback } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { Loader2, AlertCircle, Check, Puzzle, Power, PowerOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@pgs-core/ui/components/card";
import { Button } from "@pgs-core/ui/components/button";
import { Badge } from "@pgs-core/ui/components/badge";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@pgs-core/ui/components/alert-dialog";
import { formatTimestamp, formatMonthlyPence } from "@pgs-core/portal-lib/format";
import type { AdminOutletContext } from "../lib/outlet-context";

interface ModuleInfo {
  slug: string;
  name: string;
  description: string;
  monthly_pence: number;
  active: boolean;
  activated_at?: string | null;
}

/** Billing sync outcome the API returns alongside a module change. */
interface BillingNote {
  updated: boolean;
  amount_pence?: number | null;
  reason?: string;
}

function formatMonthly(pence: number | null | undefined) {
  return typeof pence === "number" ? formatMonthlyPence(pence) : "";
}

/**
 * Founder-facing module switchboard: activate runs the module's schema
 * migration against the client's D1 and bumps their GoCardless subscription;
 * deactivate is a soft delete (data kept) and drops the subscription back.
 * The billing sync outcome from the API is surfaced verbatim so a skipped or
 * failed sync is never silent.
 */
export function ClientModulesPage() {
  const { clientId } = useParams();
  const { clients, api } = useOutletContext<AdminOutletContext>();
  const client = clients.find((c) => c.id === clientId);
  const provisioned = Boolean(client?.d1_database_id);

  const [modules, setModules] = useState<ModuleInfo[] | null>(null);
  const [error, setError] = useState("");
  const [billingNote, setBillingNote] = useState<BillingNote | null>(null);
  const [confirmTarget, setConfirmTarget] =
    useState<{ module: ModuleInfo; action: "activate" | "deactivate" } | null>(null);
  const [working, setWorking] = useState(false);

  const load = useCallback(() => {
    if (!api || !clientId) return;
    api.request<{ modules: ModuleInfo[] }>(`/admin/clients/${encodeURIComponent(clientId)}/modules`)
      .then((data) => setModules(data.modules))
      .catch((err: Error) => setError(err.message));
  }, [api, clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const applyChange = async () => {
    if (!confirmTarget || !api || !clientId) return;
    const { module, action } = confirmTarget;
    setWorking(true);
    setError("");
    setBillingNote(null);
    try {
      const path = `/admin/clients/${encodeURIComponent(clientId)}/modules`;
      type ModulesResponse = { modules: ModuleInfo[]; billing?: BillingNote | null };
      const data = action === "activate"
        ? await api.request<ModulesResponse>(path, {
            method: "POST",
            body: JSON.stringify({ module: module.slug }),
          })
        : await api.request<ModulesResponse>(`${path}/${encodeURIComponent(module.slug)}`, {
            method: "DELETE",
          });
      setModules(data.modules);
      setBillingNote(data.billing ?? null);
      setConfirmTarget(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {billingNote && (
        <Alert className="mb-4" variant={billingNote.updated ? "default" : "destructive"}>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {billingNote.updated ? "Subscription updated" : "Billing not updated"}
          </AlertTitle>
          <AlertDescription>
            {billingNote.updated
              ? `The GoCardless subscription is now ${formatMonthly(billingNote.amount_pence)}.`
              : `${billingNote.reason}${billingNote.amount_pence != null
                  ? ` — the correct total is ${formatMonthly(billingNote.amount_pence)}.`
                  : ""} Reconcile in GoCardless if needed.`}
          </AlertDescription>
        </Alert>
      )}

      {!provisioned && (
        <Alert className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Client not provisioned yet</AlertTitle>
          <AlertDescription>
            Modules need the client's database — finish provisioning on the Overview tab first.
          </AlertDescription>
        </Alert>
      )}

      {!modules && !error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {modules?.map((module) => (
          <Card key={module.slug}>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Puzzle className="h-4 w-4 text-muted-foreground" />
                {module.name}
              </CardTitle>
              {module.active ? (
                <Badge variant="success" className="gap-1">
                  <Check className="h-3 w-3" />
                  Active
                </Badge>
              ) : (
                <Badge variant="secondary">Inactive</Badge>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">{module.description}</p>
              <p className="text-sm font-medium">{formatMonthly(module.monthly_pence)}</p>
              {module.active && module.activated_at && (
                <p className="text-xs text-muted-foreground">
                  Active since {formatTimestamp(module.activated_at, "")}
                </p>
              )}
              {module.active ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={working}
                  onClick={() => setConfirmTarget({ module, action: "deactivate" })}
                >
                  <PowerOff className="h-4 w-4" />
                  Deactivate
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled={working || !provisioned}
                  onClick={() => setConfirmTarget({ module, action: "activate" })}
                >
                  <Power className="h-4 w-4" />
                  Activate
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!confirmTarget} onOpenChange={(open: boolean) => !open && !working && setConfirmTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmTarget?.action === "activate"
                ? `Activate ${confirmTarget?.module.name}?`
                : `Deactivate ${confirmTarget?.module.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget?.action === "activate"
                ? `This sets up the module's tables in the client's database, switches the panel on in their portal, and raises their subscription by ${confirmTarget ? formatMonthly(confirmTarget.module.monthly_pence) : ""}. Confirm the client has agreed to the charge first.`
                : "The module disappears from the client's portal and their subscription drops back, but nothing is deleted — reactivating restores their data exactly as it was."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e: React.MouseEvent) => { e.preventDefault(); applyChange(); }}
              disabled={working}
            >
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {confirmTarget?.action === "activate" ? "Activate module" : "Deactivate module"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
